// SessionsRail — the left-side list of a user's coach sessions.
//
// Per designer handoff (Workstream 2): 260-280px wide inside the Coach
// route, border-right against the thread. Groups: Pinned → This week →
// Last week → monthly. Session items carry title (or "New session · date"
// muted italic until auto-titled), the coach-written one-line summary,
// a mono metadata row with the date and optional framework chip.
//
// Not in V1 (deferred):
//   - Virtualisation / "many" collapsed month groups (performance polish).
//   - Framework filter actually filtering (chip row stubbed with "All" only;
//     full filtering lands with MR E when the framework-name lookup exists).
//   - Mobile sheet overlay (hidden under 760px via media query; MR C.1
//     wires a hamburger).

import { useMemo, useState } from 'react';
import type { CoachProject, CoachSession, PatchCoachSessionRequest } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { ProjectsSection } from './ProjectsSection.js';
import { RailSearch } from './RailSearch.js';
import { SearchResults } from './SearchResults.js';
import { SessionMenu } from './SessionMenu.js';
import styles from './Coach.module.css';

export interface SessionsRailProps {
  sessions: readonly CoachSession[];
  activeSessionId: string | null;
  activeProjectId: string | null;
  /** Full projects list — SessionItem looks up its parent project here to
   *  render the project chip. ProjectsSection also uses it. */
  projects: readonly CoachProject[];
  onSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onSelectProject: (projectId: string | null) => void;
  onProjectsChanged?: () => void;
  /** Row-level actions (archive, move to project, unarchive) go through
   *  this one callback — SessionMenu computes the patch body, Coach.tsx
   *  calls patchCoachSession and reconciles the sessions list. */
  onPatchSession: (sessionId: string, patch: PatchCoachSessionRequest) => void;
  loading?: boolean;
}

export function SessionsRail({
  sessions,
  activeSessionId,
  activeProjectId,
  projects,
  onSelect,
  onNewSession,
  onSelectProject,
  onProjectsChanged,
  onPatchSession,
  loading,
}: SessionsRailProps) {
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const searching = searchQuery.trim().length > 0;
  const projectsById = useMemo(() => {
    const m = new Map<string, CoachProject>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);
  // Hide empty sessions ("+ New session" clicks that never got typed into)
  // unless they're the current active session — then they're a legitimate
  // just-created session the user is about to type in. Once the user sends
  // the first message, messageCount transitions to >=1 and the row stays.
  // Fully abandoned empty sessions quietly disappear from the rail on the
  // next load; the sweeper-side cleanup of these is a follow-up.
  const visibleSessions = useMemo(
    () => sessions.filter((s) => s.messageCount > 0 || s.id === activeSessionId),
    [sessions, activeSessionId],
  );

  return (
    <aside className={styles.rail}>
      <div className={styles.railh}>
        <RailSearch
          value={searchQuery}
          onChange={setSearchQuery}
          onEscape={() => setSearchQuery('')}
        />
        <button type="button" className={styles.newb} onClick={onNewSession}>
          {t('history.new')}
        </button>
      </div>
      {!searching && (
        <ProjectsSection
          activeProjectId={activeProjectId}
          onSelectProject={onSelectProject}
          {...(onProjectsChanged ? { onProjectsChanged } : {})}
        />
      )}
      <div className={styles.rlist}>
        {searching ? (
          <SearchResults
            query={searchQuery}
            activeSessionId={activeSessionId}
            projects={projects}
            onSelect={(id) => {
              setSearchQuery('');
              onSelect(id);
            }}
            onPatchSession={onPatchSession}
          />
        ) : loading ? (
          <RailLoading />
        ) : visibleSessions.length === 0 ? (
          <div className={styles.railEmpty}>{t('history.rail.empty')}</div>
        ) : (
          <RailBody
            sessions={visibleSessions}
            activeSessionId={activeSessionId}
            projects={projects}
            projectsById={projectsById}
            onSelect={onSelect}
            onPatchSession={onPatchSession}
          />
        )}
      </div>
    </aside>
  );
}

function RailLoading() {
  return (
    <div style={{ padding: 14 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} style={{ marginBottom: 18 }}>
          <div className={styles.sk} style={{ width: '80%' }} />
          <div className={styles.sk} style={{ width: '55%', height: 8, marginBottom: 0 }} />
        </div>
      ))}
    </div>
  );
}

type Group = 'pinned' | 'today' | 'yesterday' | 'previous7' | 'previous30' | string; // month key

interface GroupedSessions {
  group: Group;
  label: string;
  sessions: CoachSession[];
}

function RailBody({
  sessions,
  activeSessionId,
  projects,
  projectsById,
  onSelect,
  onPatchSession,
}: {
  sessions: readonly CoachSession[];
  activeSessionId: string | null;
  projects: readonly CoachProject[];
  projectsById: Map<string, CoachProject>;
  onSelect: (id: string) => void;
  onPatchSession: (sessionId: string, patch: PatchCoachSessionRequest) => void;
}) {
  const { t } = useI18n();
  const groups = groupSessions(sessions, t);
  return (
    <>
      {groups.map((g) => (
        <div key={g.group}>
          <div className={styles.grp}>{g.label}</div>
          {g.sessions.map((s) => (
            <SessionItem
              key={s.id}
              session={s}
              active={s.id === activeSessionId}
              project={s.projectId ? projectsById.get(s.projectId) ?? null : null}
              projects={projects}
              onSelect={() => onSelect(s.id)}
              onPatch={(patch) => onPatchSession(s.id, patch)}
            />
          ))}
        </div>
      ))}
    </>
  );
}

export interface SessionItemProps {
  session: CoachSession;
  active: boolean;
  /** Resolved project doc when the session has a projectId; `null` otherwise. */
  project: CoachProject | null;
  /** Full projects list — passed to the row kebab for the "Move to project"
   *  submenu. The projectsById lookup upstream still resolves the chip. */
  projects: readonly CoachProject[];
  onSelect: () => void;
  onPatch: (patch: PatchCoachSessionRequest) => void;
}

function SessionItem({
  session,
  active,
  project,
  projects,
  onSelect,
  onPatch,
}: SessionItemProps) {
  const { t, tt } = useI18n();
  const hasTitle = Boolean(session.title);
  const dateLabel = relativeDate(session.lastMessageAt ?? session.createdAt);

  // Nested buttons are invalid HTML. The row is a flex container with a
  // button for the main (clickable) area and the kebab button as a sibling.
  return (
    <div className={`${styles.siRow} ${active ? styles.siRowOn : ''}`}>
      <button
        type="button"
        className={`${styles.si} ${active ? styles.siOn : ''}`}
        onClick={onSelect}
      >
        {hasTitle ? (
          <>
            <div className={styles.siTitle}>{session.title}</div>
            {session.summary && <div className={styles.siSummary}>{session.summary}</div>}
          </>
        ) : (
          <div className={`${styles.siTitle} ${styles.siTitleGen}`}>
            {tt('history.untitled', { date: dateLabel })}
          </div>
        )}
        <div className={styles.siMeta}>
          <span>{dateLabel}</span>
          {project && (
            <span className={styles.projectChip} title={project.name}>
              ◇ {project.name}
            </span>
          )}
          {session.frameworkInstanceId && (
            <span className={styles.fwc}>{t('history.framework.badge')}</span>
          )}
        </div>
      </button>
      <SessionMenu session={session} projects={projects} onPatch={onPatch} />
    </div>
  );
}

// ─── Grouping + date helpers ───────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Date-group sessions the way Claude.ai does the sidebar:
 *   Pinned
 *   Today
 *   Yesterday
 *   Previous 7 Days
 *   Previous 30 Days
 *   <Month YYYY>, newest month first.
 *
 * Bucket boundaries are relative to local midnight so a session from 11pm
 * yesterday doesn't accidentally slip into Today.
 */
function groupSessions(
  sessions: readonly CoachSession[],
  t: (key: string) => string,
): GroupedSessions[] {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = startOfToday.getTime() - DAY_MS;
  const startOfPrev7 = startOfToday.getTime() - 7 * DAY_MS;
  const startOfPrev30 = startOfToday.getTime() - 30 * DAY_MS;

  const buckets: Record<string, CoachSession[]> = {};
  const order: string[] = [];

  function push(key: string, s: CoachSession) {
    if (!buckets[key]) {
      buckets[key] = [];
      order.push(key);
    }
    buckets[key]!.push(s);
  }

  for (const s of sessions) {
    if (s.pinned) {
      push('pinned', s);
      continue;
    }
    const anchor = Date.parse(s.lastMessageAt ?? s.createdAt);
    if (anchor >= startOfToday.getTime()) {
      push('today', s);
    } else if (anchor >= startOfYesterday) {
      push('yesterday', s);
    } else if (anchor >= startOfPrev7) {
      push('previous7', s);
    } else if (anchor >= startOfPrev30) {
      push('previous30', s);
    } else {
      const d = new Date(anchor);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      push(monthKey, s);
    }
  }

  return order.map((key) => {
    if (key === 'pinned') {
      return { group: 'pinned', label: t('history.group.pinned'), sessions: buckets[key]! };
    }
    if (key === 'today') {
      return { group: 'today', label: t('history.group.today'), sessions: buckets[key]! };
    }
    if (key === 'yesterday') {
      return { group: 'yesterday', label: t('history.group.yesterday'), sessions: buckets[key]! };
    }
    if (key === 'previous7') {
      return { group: 'previous7', label: t('history.group.previous7'), sessions: buckets[key]! };
    }
    if (key === 'previous30') {
      return { group: 'previous30', label: t('history.group.previous30'), sessions: buckets[key]! };
    }
    const d = new Date(key + '-01T00:00:00Z');
    return {
      group: key,
      label: formatMonthLabel(d),
      sessions: buckets[key]!,
    };
  });
}

function relativeDate(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const diffHours = Math.round(diffMs / (60 * 60 * 1000));
  if (diffHours < 1) return 'now';
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffMs / DAY_MS);
  if (diffDays < 7) {
    return new Date(then).toLocaleDateString(undefined, { weekday: 'short' });
  }
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatMonthLabel(d: Date): string {
  return d
    .toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    .toUpperCase();
}
